<!--
  Journal des envois e-Transport (bouton LOG de la liste). Chaque tentative d'envoi
  vers l'ANAF, acceptée ou refusée, est écrite côté API dans etransport_log avec le
  motif, le XML envoyé et la réponse brute de l'ANAF. Cliquer une ligne ouvre le
  détail ; « Copy report » met tout dans le presse-papier pour l'envoyer au support.
-->
<template>
  <vs-popup class="etransport-log-popup" title="e-Transport LOG" :active.sync="isActive">
    <div class="etl-toolbar">
      <vs-input v-model="search" icon="search" icon-after class="etl-search"
        placeholder="Search user / UIT / plate / partner / message / invoice id…"
        @keyup.enter.native="fetchLogs" />
      <select v-model="outcomeFilter" class="etl-select" @change="fetchLogs">
        <option value="">All results</option>
        <option value="accepted">Accepted</option>
        <option value="refused">Refused by ANAF</option>
        <option value="error">Technical error</option>
      </select>
      <vs-button size="small" type="border" icon="refresh" @click="fetchLogs">Refresh</vs-button>
    </div>

    <div class="etl-status">
      <span v-if="loading">Loading…</span>
      <span v-else-if="error" class="etl-error">{{ error }}</span>
      <span v-else>
        {{ entries.length }} attempt{{ entries.length === 1 ? '' : 's' }}
        <span v-if="entries.length >= limit" class="etl-muted"> (capped at {{ limit }} — narrow the search)</span>
      </span>
    </div>

    <div class="etl-table-wrap">
      <table class="etl-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>User</th>
            <th>Result</th>
            <th>UIT</th>
            <th>Type</th>
            <th>Plate</th>
            <th>Partner</th>
            <th>Linked</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && entries.length === 0">
            <td colspan="9" class="etl-empty">No e-Transport attempt recorded yet.</td>
          </tr>
          <template v-for="log in entries">
            <tr :key="'r' + log.id" class="etl-row" @click="toggle(log)">
              <td class="etl-nowrap">{{ formatDate(log.created_at) }}</td>
              <td class="etl-nowrap">{{ log.who || '—' }}</td>
              <td class="etl-nowrap">
                <span class="etl-pill" :class="'etl-' + result(log).kind">{{ result(log).label }}</span>
              </td>
              <td class="etl-nowrap">{{ log.uit || '—' }}</td>
              <td class="etl-nowrap">{{ typeLabel(log.op_type) }}</td>
              <td class="etl-nowrap">{{ log.plate || '—' }}</td>
              <td>{{ log.partner || '—' }}</td>
              <td class="etl-nowrap">
                <span v-if="log.idtransport">T#{{ log.idtransport }}</span>
                <span v-if="log.idinvoice"> I#{{ log.idinvoice }}</span>
                <span v-if="!log.idtransport && !log.idinvoice" class="etl-muted" title="Nothing selected in Copy … Data: the UIT is not saved in the ERP">none</span>
              </td>
              <td class="etl-message">{{ shorten(log.error_message) }}</td>
            </tr>
            <tr v-if="openId === log.id" :key="'d' + log.id" class="etl-detail-row">
              <td colspan="9">
                <div v-if="detailLoading">Loading…</div>
                <div v-else-if="detail">
                  <div class="etl-detail-head">
                    <span>
                      <strong>#{{ detail.id }}</strong> · stage <strong>{{ detail.stage || '—' }}</strong>
                      · HTTP <strong>{{ detail.http_status || '—' }}</strong>
                      <span v-if="detail.upload_index"> · upload index <strong>{{ detail.upload_index }}</strong></span>
                      <span v-if="detail.transport_date"> · transport date <strong>{{ detail.transport_date }}</strong></span>
                    </span>
                    <vs-button size="small" icon="content_copy" @click.stop="copyReport(detail)">Copy report</vs-button>
                  </div>
                  <div v-if="detail.error_message" class="etl-block">
                    <div class="etl-block-title">Message</div>
                    <pre class="etl-pre etl-pre-error">{{ detail.error_message }}</pre>
                  </div>
                  <div v-if="detail.anaf_response" class="etl-block">
                    <div class="etl-block-title">ANAF response</div>
                    <pre class="etl-pre">{{ detail.anaf_response }}</pre>
                  </div>
                  <div v-if="detail.xml_sent" class="etl-block">
                    <div class="etl-block-title">XML sent</div>
                    <pre class="etl-pre">{{ detail.xml_sent }}</pre>
                  </div>
                </div>
                <div v-else class="etl-error">{{ detailError || 'Could not load this entry.' }}</div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </vs-popup>
</template>

<script>
import axios from "@/axios.js";
import moment from "moment";

const TYPE_LABELS = {
  10: "10 Achiz. intracom.",
  20: "20 Livrare intracom.",
  30: "30 Transport national",
  40: "40 Import",
  50: "50 Export",
  60: "60 Intrare depozit",
  70: "70 Iesire depozit",
};

export default {
  name: "ETransportLog",
  props: {
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      entries: [],
      loading: false,
      error: "",
      search: "",
      outcomeFilter: "",
      limit: 300,
      openId: null,
      detail: null,
      detailLoading: false,
      detailError: "",
    };
  },
  computed: {
    // Proxy so the parent can use :active.sync on this component.
    isActive: {
      get() { return this.active; },
      set(val) { this.$emit("update:active", val); },
    },
  },
  watch: {
    active(val) {
      if (val) this.fetchLogs();
    },
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      this.error = "";
      this.openId = null;
      try {
        const params = { limit: this.limit };
        if (this.search) params.q = this.search;
        if (this.outcomeFilter) params.outcome = this.outcomeFilter;
        const res = await axios.get("/anaf/etransport/log", { params });
        this.entries = (res.data && res.data.entries) || [];
      } catch (e) {
        this.entries = [];
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          e.message ||
          "Failed to load the log.";
      } finally {
        this.loading = false;
      }
    },
    async toggle(log) {
      if (this.openId === log.id) { this.openId = null; return; }
      this.openId = log.id;
      this.detail = null;
      this.detailError = "";
      this.detailLoading = true;
      try {
        const res = await axios.get(`/anaf/etransport/log/${log.id}`);
        // Une autre ligne a pu être ouverte entre-temps : ne pas écraser son détail.
        if (this.openId === log.id) this.detail = res.data;
      } catch (e) {
        if (this.openId === log.id) {
          this.detailError = (e.response && e.response.data && e.response.data.message) || e.message;
        }
      } finally {
        if (this.openId === log.id) this.detailLoading = false;
      }
    },
    result(log) {
      if (log.outcome === "accepted") {
        return log.stage === "erp_save"
          ? { kind: "warn", label: "ACCEPTED - not saved in ERP" }
          : { kind: "ok", label: "ACCEPTED" };
      }
      if (log.outcome === "refused") return { kind: "ko", label: "REFUSED" };
      return { kind: "err", label: "ERROR" };
    },
    typeLabel(code) {
      return code ? (TYPE_LABELS[code] || code) : "—";
    },
    formatDate(iso) {
      const m = moment(iso);
      return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : "—";
    },
    shorten(text) {
      if (!text) return "";
      return text.length > 140 ? `${text.slice(0, 140)}…` : text;
    },
    reportText(d) {
      const lines = [
        `e-Transport attempt #${d.id} - ${this.formatDate(d.created_at)}`,
        `User: ${d.who || "-"}`,
        `Result: ${this.result(d).label} (stage ${d.stage || "-"}, HTTP ${d.http_status || "-"})`,
        `UIT: ${d.uit || "-"}   Upload index: ${d.upload_index || "-"}`,
        `Type: ${this.typeLabel(d.op_type)}   Plate: ${d.plate || "-"}   Partner: ${d.partner || "-"}   Transport date: ${d.transport_date || "-"}`,
        `Linked transport: ${d.idtransport || "none"}   Linked invoice: ${d.idinvoice || "none"}`,
        "",
        "MESSAGE:",
        d.error_message || "-",
        "",
        "ANAF RESPONSE:",
        d.anaf_response || "-",
        "",
        "XML SENT:",
        d.xml_sent || "-",
      ];
      return lines.join("\n");
    },
    async copyReport(d) {
      const text = this.reportText(d);
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        // Contexte non sécurisé ou permission refusée : repli par sélection.
        const area = document.createElement("textarea");
        area.value = text;
        document.body.appendChild(area);
        area.select();
        document.execCommand("copy");
        document.body.removeChild(area);
      }
      this.$vs.notify({ title: "Copied", text: "Report copied to the clipboard", color: "success", time: 3000 });
    },
  },
};
</script>

<style>
/* vs-popup renders to body, so target it un-scoped to widen the dialog. */
.etransport-log-popup .vs-popup {
  width: 94vw;
  max-width: 1300px;
}
.etransport-log-popup .vs-popup--content {
  max-height: 80vh;
}
</style>

<style scoped>
.etl-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-bottom: 10px; }
.etl-search { flex: 1 1 320px; }
.etl-select {
  height: 38px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #4b5563;
}
.etl-status { font-size: 0.8rem; color: #6b7280; margin-bottom: 8px; }
.etl-error { color: #ef4444; }
.etl-muted { color: #9ca3af; }

.etl-table-wrap { overflow: auto; max-height: 62vh; border: 1px solid #e5e7eb; border-radius: 6px; }
.etl-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.etl-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f9fafb;
  padding: 0.6rem 0.75rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  border-bottom: 1px solid #d1d5db;
}
.etl-table tbody td { padding: 0.55rem 0.75rem; border-bottom: 1px solid #eef0f2; vertical-align: top; }
.etl-row { cursor: pointer; }
.etl-row:hover { background: #f9fafb; }
.etl-nowrap { white-space: nowrap; }
.etl-message { max-width: 360px; word-break: break-word; }
.etl-empty { text-align: center; color: #9ca3af; padding: 1.5rem; }

.etl-pill {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #fff;
}
.etl-ok { background: #28c76f; }
.etl-ko { background: #ea5455; }
.etl-err { background: #ff9f43; }
.etl-warn { background: #ff9f43; }

.etl-detail-row td { background: #f9fafb; }
.etl-detail-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 8px; }
.etl-block { margin-bottom: 8px; }
.etl-block-title { font-size: 0.7rem; font-weight: 600; text-transform: uppercase; color: #6b7280; margin-bottom: 2px; }
.etl-pre {
  margin: 0;
  padding: 8px 10px;
  max-height: 260px;
  overflow: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.etl-pre-error { border-color: #ea5455; }
</style>
